import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Chip, Button, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField
} from '@mui/material';

const SupplierQuotes = () => {
  const { t } = useTranslation();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [respondDialog, setRespondDialog] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [supplierPrice, setSupplierPrice] = useState('');
  const [supplierNotes, setSupplierNotes] = useState('');

  useEffect(() => { loadQuotes(); }, []);

  const loadQuotes = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/quotes');
      setQuotes(data.data || []);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const handleRespond = async () => {
    try {
      await api.put(`/quotes/${selectedQuote._id}/respond`, {
        supplierPrice: Number(supplierPrice),
        supplierNotes
      });
      setRespondDialog(false);
      loadQuotes();
    } catch (err) { console.error(err); }
  };

  const statusColors = {
    pending: 'warning', supplier_responded: 'info', admin_reviewed: 'primary',
    sent_to_customer: 'secondary', accepted: 'success', rejected: 'error'
  };

  if (loading) return <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>{t('quotes')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Respond with EXW (Ex Works) pricing. OSESG will add commission and logistics costs.
      </Typography>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f5f5f5' }}>
              <TableCell>Quote #</TableCell>
              <TableCell>Product</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Your Price</TableCell>
              <TableCell>Date</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {quotes.map((quote) => (
              <TableRow key={quote._id} hover>
                <TableCell><strong>{quote.quoteNumber}</strong></TableCell>
                <TableCell>{quote.product?.name || 'N/A'}</TableCell>
                <TableCell>{quote.quantity}</TableCell>
                <TableCell>
                  <Chip label={quote.status} size="small" color={statusColors[quote.status] || 'default'} />
                </TableCell>
                <TableCell>{quote.supplierPrice ? `$${quote.supplierPrice.toLocaleString()}` : '-'}</TableCell>
                <TableCell>{new Date(quote.createdAt).toLocaleDateString()}</TableCell>
                <TableCell>
                  {quote.status === 'pending' && (
                    <Button size="small" variant="contained" onClick={() => {
                      setSelectedQuote(quote);
                      setSupplierPrice('');
                      setSupplierNotes('');
                      setRespondDialog(true);
                    }}>
                      Respond
                    </Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={respondDialog} onClose={() => setRespondDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Respond to Quote: {selectedQuote?.quoteNumber}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" gutterBottom>
            Product: {selectedQuote?.product?.name} | Qty: {selectedQuote?.quantity}
          </Typography>
          <TextField fullWidth label="Your EXW Price ($)" type="number" value={supplierPrice}
            onChange={(e) => setSupplierPrice(e.target.value)} sx={{ mt: 2 }} />
          <TextField fullWidth multiline rows={3} label="Notes" value={supplierNotes}
            onChange={(e) => setSupplierNotes(e.target.value)} sx={{ mt: 2 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRespondDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleRespond}>Submit Response</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupplierQuotes;
